import { toSingleLinePlainText } from '../text/plain-text-value'

/** @typedef {{ id: string, type: string, data: Record<string, unknown> }} TabBlock */
/** @typedef {{ id: string, label: string, blocks: TabBlock[] }} NotebookTab */
/** @typedef {{ activeTab: string, tabs: NotebookTab[] }} TabsBlockData */

export const MAX_NOTEBOOK_TABS = 8
export const VISIBLE_TAB_SLOTS = 5
/** Max tab title chars — fits 4-tab strip width without resize/wrap (12.5px / 7×16px pads). */
export const TAB_TITLE_MAX_LENGTH = 14

export function clampTabTitle(label, max = TAB_TITLE_MAX_LENGTH) {
  return toSingleLinePlainText(label, max)
}

export function createTab(label = 'Tab 1') {
  return {
    id: crypto.randomUUID(),
    label,
    blocks: [],
  }
}

export function createEmptyTabsBlockData() {
  const first = createTab('Tab 1')
  return {
    activeTab: first.id,
    tabs: [first],
  }
}

/** @param {unknown} data */
export function normalizeTabsBlockData(data) {
  if (!data || typeof data !== 'object') return createEmptyTabsBlockData()
  const d = /** @type {TabsBlockData} */ (data)
  const tabs = Array.isArray(d.tabs)
    ? d.tabs
        .filter((t) => t && typeof t.id === 'string')
        .map((t) => ({
          id: String(t.id),
          label:
            typeof t.label === 'string' ? clampTabTitle(t.label) : '',
          blocks: Array.isArray(t.blocks) ? t.blocks : [],
        }))
    : []
  if (!tabs.length) return createEmptyTabsBlockData()
  const activeTab =
    typeof d.activeTab === 'string' && tabs.some((t) => t.id === d.activeTab)
      ? d.activeTab
      : tabs[0].id
  return { activeTab, tabs }
}

/** @param {TabsBlockData} data @param {string} tabId */
export function tabHasContent(data, tabId) {
  const tab = data.tabs.find((t) => t.id === tabId)
  return Boolean(tab?.blocks?.length)
}

/** Persist only tab strip metadata in notebook_sections.data (not child blocks). */
export function stripTabsMetadata(data) {
  const d = normalizeTabsBlockData(data)
  return {
    activeTab: d.activeTab,
    tabs: d.tabs.map(({ id, label }) => ({ id, label })),
  }
}

/**
 * Merge notebook_blocks rows into tabs section runtime shape.
 * @param {unknown} data
 * @param {Array<{ id: string, tab_id: string|null, type: string, content?: unknown, sort_key?: string }>} apiBlocks
 */
export function mergeTabBlocksIntoSectionData(data, apiBlocks) {
  const d = normalizeTabsBlockData(data)
  if (!apiBlocks?.length) return d

  /** @type {Map<string, typeof apiBlocks>} */
  const byTab = new Map()
  for (const row of apiBlocks) {
    if (!row.tab_id) continue
    const list = byTab.get(row.tab_id) ?? []
    list.push(row)
    byTab.set(row.tab_id, list)
  }

  return {
    ...d,
    tabs: d.tabs.map((tab) => {
      const rows = (byTab.get(tab.id) ?? []).sort((a, b) =>
        (a.sort_key ?? '') < (b.sort_key ?? '') ? -1 : 1,
      )
      if (!rows.length) return tab
      return {
        ...tab,
        blocks: rows.map((r) => ({
          id: r.id,
          type: r.type,
          data:
            r.content && typeof r.content === 'object'
              ? /** @type {Record<string, unknown>} */ (r.content)
              : {},
          sort_key: r.sort_key,
        })),
      }
    }),
  }
}
